import React, { useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import { View, Text, Pressable } from '../lib/react-native-compat';

// ── The coverage grid ──
//
// The Daily Field Tickets view, days across: one column per work day, one
// block of rows per month (the 1st under the 1st, so a month reads on one
// line and the page needs no sideways scroll), and in each block a row for
// each thing the day owes — the HeavyJob hours on it, the day and
// night field tickets, the week summary over the week and the invoice
// billing it. The question the page answers is "which days have time in
// HeavyJob that nothing has gone out for", so a day with hours and no ticket
// reads MISSING in red, a week with hours and no summary likewise, and a
// send that is out shows how many of its signers have signed.
//
// The grid itself does nothing but select: clicking a cell puts it in the
// detail strip under the grid, where everything you ever did to it lives —
// preview, export, open it on the Ticket Map, open the signed copy, the
// day's SharePoint scans. Presentational: the model (`cols`, `rows`) is
// built by billingTimecardApproval, which owns the data.
//
// Colors are the Strike template's status set: good / info / bad / caution /
// neutral, each with its word. Hours take the navy ramp, light to dark.

export const COL_W = 46;
const LABEL_W = 112;
const ROW_H = 30;
const LUMP_SPAN = 7;

export const TONES = {
    good: { bg: '#e9f6ee', fg: '#166534' },
    out: { bg: '#e8eef5', fg: '#1d518c' },
    bad: { bg: '#fbeaea', fg: '#b02329' },
    warn: { bg: '#fdf4dc', fg: '#854d0e' },
    draft: { bg: '#f5f5f5', fg: '#525252' },
    none: { bg: 'transparent', fg: '#a3a3a3' },
};
export const RAMP = ['#f5f5f5', '#dbe7f4', '#b5cde8', '#88aed8', '#5a8cc4'];
// Fixed steps, so a 700-hour day reads the same on every job.
export const rampLevel = (h) => (!h ? 0 : h < 100 ? 1 : h < 400 ? 2 : h < 800 ? 3 : 4);

const lbl = { fontSize: 12, fontWeight: '500', color: '#737373', whiteSpace: 'nowrap' };

/**
 * A cell: a colored chip with its word. `span` columns wide (1 by default).
 * `sel` draws the selected ring. Pressable when `onPress` is given; a cell
 * with nothing on it is not. A one-day cell sets its word at 11px so
 * "Missing" and "Opened" fit the narrow column whole.
 */
const Cell = ({ span = 1, tone = 'none', label, sub, title, onPress, sel, strong }) => {
    const t = TONES[tone] || TONES.none;
    const inner = (
        <View
            title={title}
            style={{
                width: COL_W * span - 2, height: ROW_H - 2, marginLeft: 1, marginRight: 1,
                borderRadius: 4, backgroundColor: t.bg,
                alignItems: 'center', justifyContent: 'center',
                borderWidth: 1, borderStyle: 'solid', borderColor: sel ? '#1d518c' : 'transparent',
                boxShadow: sel ? '0 0 0 2px rgba(29, 81, 140, 0.3)' : 'none',
                cursor: onPress ? 'pointer' : 'default',
            }}
        >
            <Text style={{ fontSize: span > 1 ? 12 : 11, fontWeight: strong ? '500' : '400', color: t.fg, whiteSpace: 'nowrap', lineHeight: 14, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Text>
            {sub ? <Text style={{ fontSize: 11, color: t.fg, whiteSpace: 'nowrap', lineHeight: 12, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</Text> : null}
        </View>
    );
    return onPress ? <Pressable onPress={onPress} style={{ flexShrink: 0 }}>{inner}</Pressable> : <View style={{ flexShrink: 0 }}>{inner}</View>;
};

/** A row label on the left, then its cells. */
// The label column stays put while the days scroll under it.
const sticky = { position: 'sticky', left: 0, zIndex: 1, backgroundColor: '#ffffff' };
const Row = ({ label, title, children, top }) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', height: ROW_H, borderTopWidth: top ? 1 : 0, borderTopStyle: 'solid', borderTopColor: '#e5e5e5' }}>
        <Text title={title} style={{ ...lbl, ...sticky, width: LABEL_W, flexShrink: 0, color: '#525252', fontWeight: '400', lineHeight: `${ROW_H}px` }}>{label}</Text>
        {children}
    </View>
);

/**
 * cols   [{ d, we }] oldest first — `we` the week ending the day falls in.
 *        Split into one block per month here; a span that crosses a month
 *        end is drawn in both blocks.
 * rows   {
 *          hours:   { [d]: { label, sub, title, level } }
 *          sheets:  { Day: { [d]: cell }, Night: { [d]: cell } | null }   cell = { label, tone, title, blank }
 *          docs:    { [d]: { count, title } }
 *          summary: [{ from, to, label, sub, tone, title, key }]   from/to are column indexes
 *          invoice: [{ from, to, label, sub, tone, title, key }]
 *          lumps:   [{ label, sub, tone, title, key }]   invoices that bill no day
 *        }
 * selected   { kind: 'sheet'|'summary'|'invoice'|'lump'|'day', d, sh, key }
 * onSelect   (selection) => void
 * detail     the strip's content for the selection, built by the owner
 */
export default function CoverageGrid({ cols, rows, selected, onSelect, detail }) {
    const scroller = useRef(null);
    // One block per calendar month, each day at its day-of-month slot. The
    // columns are consecutive days, so a block's are cols[first..last].
    const months = useMemo(() => {
        const out = [];
        cols.forEach((c, i) => {
            const m = moment(c.d, 'YYYY-MM-DD');
            const key = m.format('YYYY-MM');
            let blk = out[out.length - 1];
            if (!blk || blk.key !== key) {
                blk = { key, name: m.format('MMMM YYYY'), first: i, last: i, cols: [] };
                out.push(blk);
            }
            blk.cols.push({ ...c, slot: m.date() - 1 });
            blk.last = i;
        });
        return out;
    }, [cols]);
    const slots = months.reduce((n, b) => Math.max(n, b.cols[b.cols.length - 1].slot + 1), 0);

    // On a screen too narrow for a whole month, start the eye on the newest day.
    useEffect(() => {
        const el = scroller.current;
        const blk = months[months.length - 1];
        if (!el || !blk) return;
        el.scrollLeft = Math.max(0, LABEL_W + (blk.cols[blk.cols.length - 1].slot + 1) * COL_W - el.clientWidth);
    }, [months]);

    const is = (s) => Boolean(selected) && Object.keys(s).every((k) => selected[k] === s[k]);
    const gap = (key, n) => <View key={key} style={{ width: COL_W * n, flexShrink: 0 }} />;
    // A block's days at their slots, blank columns before the first.
    const dayCells = (blk, render) => {
        const out = [];
        let at = 0;
        blk.cols.forEach((c) => {
            if (c.slot > at) out.push(gap(`gap${at}`, c.slot - at));
            out.push(render(c));
            at = c.slot + 1;
        });
        return out;
    };
    // Spans are column indexes across the whole grid, clipped to the block.
    const spanCells = (spans, kind, blk) => {
        const out = [];
        let at = 0;
        const slotOf = (i) => blk.cols[i - blk.first].slot;
        spans.forEach((s) => {
            if (s.to < blk.first || s.from > blk.last) return;
            const a = slotOf(Math.max(s.from, blk.first));
            const b = slotOf(Math.min(s.to, blk.last));
            if (a > at) out.push(gap(`gap${at}`, a - at));
            out.push(
                <Cell
                    key={s.key || `${kind}${s.from}`}
                    span={b - a + 1}
                    tone={s.tone}
                    label={s.label}
                    sub={s.sub}
                    title={s.title}
                    strong
                    sel={is({ kind, key: s.key })}
                    onPress={s.selectable === false ? undefined : () => onSelect({ kind, key: s.key, d: cols[s.from]?.d })}
                />,
            );
            at = b + 1;
        });
        return out;
    };

    const swatch = (bg, text, border) => (
        <View key={text} style={{ flexDirection: 'row', alignItems: 'center', marginRight: 14, marginBottom: 4 }}>
            <View style={{ width: 14, height: 12, borderRadius: 3, backgroundColor: bg, borderWidth: 1, borderStyle: 'solid', borderColor: border || '#e5e5e5', marginRight: 6 }} />
            <Text style={{ fontSize: 12, color: '#525252' }}>{text}</Text>
        </View>
    );
    const monthBlock = (blk, n) => (
        <View key={blk.key} style={{ marginTop: n ? 14 : 0 }}>
            {/* Header: the month, then each date over its weekday. */}
            <View style={{ flexDirection: 'row', alignItems: 'flex-end', height: 34, borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: '#d4d4d4' }}>
                <Text style={{ ...lbl, ...sticky, width: LABEL_W, flexShrink: 0, lineHeight: '34px', fontWeight: '600', color: '#0a0a0a' }}>{blk.name}</Text>
                {dayCells(blk, (c) => {
                    const m = moment(c.d, 'YYYY-MM-DD');
                    const sunday = m.day() === 0;
                    return (
                        <View
                            key={c.d}
                            title={m.format('ddd M/D/YY')}
                            style={{
                                width: COL_W, flexShrink: 0, alignItems: 'center', paddingBottom: 3,
                            }}
                        >
                            <Text style={{ fontSize: 12, fontWeight: '500', color: sunday ? '#0a0a0a' : '#737373', whiteSpace: 'nowrap' }}>{m.format('M/D')}</Text>
                            <Text style={{ fontSize: 11, color: '#737373' }}>{m.format('ddd')}</Text>
                        </View>
                    );
                })}
            </View>

            <Row label="HeavyJob hours" title="Hours on the day — the live HeavyJob cards, else HEET. Darker is more; hover for the headcount.">
                {dayCells(blk, (c) => {
                    const h = rows.hours[c.d];
                    const level = h ? h.level : 0;
                    return (
                        <Pressable key={c.d} onPress={() => onSelect({ kind: 'day', d: c.d })} style={{ flexShrink: 0 }}>
                            <View
                                title={h ? h.title : 'no HeavyJob time on this day'}
                                style={{
                                    width: COL_W - 2, height: ROW_H - 2, marginLeft: 1, marginRight: 1, borderRadius: 4,
                                    backgroundColor: h ? RAMP[level] : 'transparent',
                                    alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                    borderWidth: 1, borderStyle: 'solid', borderColor: is({ kind: 'day', d: c.d }) ? '#1d518c' : 'transparent',
                                }}
                            >
                                <Text style={{ fontSize: 12, fontWeight: '500', lineHeight: 14, color: !h ? '#a3a3a3' : level >= 4 ? '#ffffff' : level === 0 ? '#737373' : h.warn ? '#854d0e' : '#0a0a0a', whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h ? h.label : ''}</Text>
                            </View>
                        </Pressable>
                    );
                })}
            </Row>

            {['Day', 'Night'].filter((sh) => rows.sheets[sh]).map((sh) => (
                <Row key={sh} label={sh === 'Day' ? 'Day ticket' : 'Night ticket'} title={`The ${sh.toLowerCase()} crew’s field ticket`}>
                    {dayCells(blk, (c) => {
                        const cell = rows.sheets[sh][c.d];
                        if (!cell || cell.blank) return gap(c.d, 1);
                        // "2/3 signed" is too wide for one day: the count over the word.
                        const part = /^(\d+\/\d+) signed$/.exec(cell.label || '');
                        return (
                            <Cell
                                key={c.d}
                                tone={cell.tone}
                                label={part ? part[1] : cell.label}
                                sub={part ? 'signed' : undefined}
                                title={`${sh} sheet — ${moment(c.d, 'YYYY-MM-DD').format('M/D/YY')}: ${cell.title}`}
                                strong={cell.tone !== 'none'}
                                sel={is({ kind: 'sheet', d: c.d, sh })}
                                onPress={() => onSelect({ kind: 'sheet', d: c.d, sh })}
                            />
                        );
                    })}
                </Row>
            ))}

            {Object.keys(rows.docs).length ? (
            <Row label="Scans filed" title="Scans filed for the day in the job's SharePoint Timecards folder">
                {dayCells(blk, (c) => {
                    const x = rows.docs[c.d];
                    if (!x || !x.count) return gap(c.d, 1);
                    return (
                        <Cell
                            key={c.d}
                            tone="draft"
                            label={`${x.count} doc${x.count > 1 ? 's' : ''}`}
                            title={x.title}
                            sel={is({ kind: 'day', d: c.d })}
                            onPress={() => onSelect({ kind: 'day', d: c.d })}
                        />
                    );
                })}
            </Row>
            ) : null}

            <Row label="Week summary" title="The week summary that went out over these days" top>
                {spanCells(rows.summary, 'summary', blk)}
            </Row>
            <Row label="Invoice approval" title="The invoice billing these days, and where its approval stands">
                {spanCells(rows.invoice, 'invoice', blk)}
            </Row>
        </View>
    );

    return (
        <View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                {swatch('#88aed8', 'HeavyJob hours (darker is more)')}
                {swatch(TONES.good.bg, 'Signed')}
                {swatch(TONES.out.bg, 'Sent, not yet signed')}
                {swatch(TONES.bad.bg, 'Missing: hours in HeavyJob, nothing sent')}
                {swatch(TONES.warn.bg, 'Week still open')}
            </View>
            <View ref={scroller} style={{ overflowX: 'auto', paddingBottom: 4 }}>
                <View style={{ minWidth: LABEL_W + COL_W * slots }}>
                    {months.map(monthBlock)}
                    {/* Lump sums (a mobilization) bill no day: once, under the months, a few days wide each. */}
                    {(rows.lumps || []).length ? (
                        <View style={{ marginTop: 14 }}>
                            <Row label="Lump-sum invoice" title="Invoices that bill no work day, such as a mobilization" top>
                                {rows.lumps.map((x) => (
                                    <Cell
                                        key={x.key}
                                        span={LUMP_SPAN}
                                        tone={x.tone}
                                        label={x.label}
                                        sub={x.sub}
                                        title={x.title}
                                        strong
                                        sel={is({ kind: 'lump', key: x.key })}
                                        onPress={() => onSelect({ kind: 'lump', key: x.key })}
                                    />
                                ))}
                            </Row>
                        </View>
                    ) : null}
                </View>
            </View>
            {detail ? (
                <View style={{ marginTop: 10, paddingTop: 10, paddingBottom: 8, paddingLeft: 12, paddingRight: 12, borderRadius: 10, backgroundColor: '#fafafa', borderWidth: 1, borderStyle: 'solid', borderColor: '#e5e5e5' }}>
                    {detail}
                </View>
            ) : null}
        </View>
    );
}
